import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import * as api from './crmAPI';

// ─── Custom Fields Thunks ─────────────────────────────────────────────────────

export const fetchCustomFields = createAsyncThunk('crm/fetchCustomFields', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getCustomFields(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const saveCustomField = createAsyncThunk('crm/saveCustomField', async ({ profileId, fieldId, data }, { rejectWithValue }) => {
  try {
    if (fieldId) {
      await api.updateCustomField(profileId, fieldId, data);
      return { ...data, Id: fieldId };
    } else {
      const res = await api.createCustomField(profileId, data);
      return { ...data, Id: res.data?.Id };
    }
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const removeCustomField = createAsyncThunk('crm/removeCustomField', async ({ profileId, fieldId }, { rejectWithValue }) => {
  try {
    await api.deleteCustomField(profileId, fieldId);
    return fieldId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const reorderCustomFields = createAsyncThunk('crm/reorderCustomFields', async ({ profileId, ids }, { rejectWithValue }) => {
  try {
    await api.reorderCustomFields(profileId, ids);
    return ids;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchContactCustomFields = createAsyncThunk('crm/fetchContactCustomFields', async ({ profileId, audienceId }, { rejectWithValue }) => {
  try {
    const res = await api.getContactCustomFields(profileId, audienceId);
    return { audienceId, values: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const saveContactCustomFields = createAsyncThunk('crm/saveContactCustomFields', async ({ profileId, audienceId, values }, { rejectWithValue }) => {
  try {
    await api.saveContactCustomFields(profileId, audienceId, values);
    return { audienceId, values };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// ─── Activities Thunks ────────────────────────────────────────────────────────

export const fetchActivities = createAsyncThunk('crm/fetchActivities', async ({ profileId, audienceId }, { rejectWithValue }) => {
  try {
    const res = await api.getActivities(profileId, audienceId);
    return { audienceId, activities: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const addActivity = createAsyncThunk('crm/addActivity', async ({ profileId, audienceId, data }, { rejectWithValue }) => {
  try {
    const res = await api.createActivity(profileId, audienceId, data);
    return { audienceId, activity: { ...data, Id: res.data?.Id, CreatedAt: new Date().toISOString(), IsAutomatic: false } };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const removeActivity = createAsyncThunk('crm/removeActivity', async ({ profileId, audienceId, activityId }, { rejectWithValue }) => {
  try {
    await api.deleteActivity(profileId, audienceId, activityId);
    return { audienceId, activityId };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const completeActivity = createAsyncThunk('crm/completeActivity', async ({ profileId, audienceId, activityId }, { rejectWithValue }) => {
  try {
    await api.completeTask(profileId, audienceId, activityId);
    return { audienceId, activityId };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// ─── Tags Thunks ─────────────────────────────────────────────────────────────

export const fetchProfileTags = createAsyncThunk('crm/fetchProfileTags', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getProfileTags(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchContactTags = createAsyncThunk('crm/fetchContactTags', async ({ profileId, audienceId }, { rejectWithValue }) => {
  try {
    const res = await api.getContactTags(profileId, audienceId);
    return { audienceId, tags: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const addContactTag = createAsyncThunk('crm/addContactTag', async ({ profileId, audienceId, name }, { rejectWithValue }) => {
  try {
    const res = await api.addContactTag(profileId, audienceId, { Name: name });
    return { audienceId, tags: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const removeContactTag = createAsyncThunk('crm/removeContactTag', async ({ profileId, audienceId, tagId }, { rejectWithValue }) => {
  try {
    const res = await api.removeContactTag(profileId, audienceId, tagId);
    return { audienceId, tags: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const deleteProfileTag = createAsyncThunk('crm/deleteProfileTag', async ({ profileId, tagId }, { rejectWithValue }) => {
  try {
    await api.deleteProfileTag(profileId, tagId);
    return tagId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// ─── Pipeline Thunks ──────────────────────────────────────────────────────────

export const fetchPipelines = createAsyncThunk('crm/fetchPipelines', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getPipelines(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchPipelineWithDeals = createAsyncThunk('crm/fetchPipelineWithDeals', async ({ profileId, pipelineId }, { rejectWithValue }) => {
  try {
    const res = await api.getPipelineWithDeals(profileId, pipelineId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const addDeal = createAsyncThunk('crm/addDeal', async ({ profileId, data }, { rejectWithValue }) => {
  try {
    const res = await api.createDeal(profileId, data);
    return { ...data, Id: res.data?.Id, CreatedAt: new Date().toISOString() };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const editDeal = createAsyncThunk('crm/editDeal', async ({ profileId, dealId, data }, { rejectWithValue }) => {
  try {
    await api.updateDeal(profileId, dealId, data);
    return { ...data, Id: dealId };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const moveDeal = createAsyncThunk('crm/moveDeal', async ({ profileId, dealId, targetStageId, sortOrder }, { rejectWithValue }) => {
  try {
    await api.moveDeal(profileId, dealId, { TargetStageId: targetStageId, SortOrder: sortOrder });
    return { dealId, targetStageId, sortOrder };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const removeDeal = createAsyncThunk('crm/removeDeal', async ({ profileId, dealId, stageId }, { rejectWithValue }) => {
  try {
    await api.deleteDeal(profileId, dealId);
    return { dealId, stageId };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchContactDeals = createAsyncThunk('crm/fetchContactDeals', async ({ profileId, audienceId }, { rejectWithValue }) => {
  try {
    const res = await api.getContactDeals(profileId, audienceId);
    return { audienceId, deals: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchCrmDashboard = createAsyncThunk('crm/fetchDashboard', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getDashboard(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// ─── Slice ────────────────────────────────────────────────────────────────────

const crmSlice = createSlice({
  name: 'crm',
  initialState: {
    // Custom fields
    customFields: [],
    customFieldsLoading: false,
    // Per-contact field values: { [audienceId]: [...] }
    contactFieldValues: {},
    contactFieldValuesLoading: false,
    // Activities: { [audienceId]: [...] }
    activities: {},
    activitiesLoading: false,
    // Pipeline
    pipelines: [],
    activePipeline: null,
    pipelinesLoading: false,
    pipelineLoading: false,
    // Per-contact deals: { [audienceId]: [...] }
    contactDeals: {},
    // Tags
    profileTags: [],
    profileTagsLoading: false,
    // Per-contact tags: { [audienceId]: [...] }
    contactTags: {},
    // Dashboard
    dashboard: null,
    dashboardLoading: false,
    error: null,
  },
  reducers: {
    // Optimistic deal move
    moveDealOptimistic(state, { payload: { dealId, fromStageId, targetStageId, sortOrder } }) {
      if (!state.activePipeline) return;
      const fromStage = state.activePipeline.Stages?.find((s) => s.Id === fromStageId);
      const toStage = state.activePipeline.Stages?.find((s) => s.Id === targetStageId);
      if (!fromStage || !toStage) return;
      const dealIdx = fromStage.Deals?.findIndex((d) => d.Id === dealId);
      if (dealIdx === -1) return;
      const [deal] = fromStage.Deals.splice(dealIdx, 1);
      deal.StageId = targetStageId;
      deal.SortOrder = sortOrder;
      toStage.Deals = toStage.Deals || [];
      toStage.Deals.splice(sortOrder, 0, deal);
    },
    clearCrmError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Custom Fields ──
      .addCase(fetchCustomFields.pending, (state) => {
        state.customFieldsLoading = true;
      })
      .addCase(fetchCustomFields.fulfilled, (state, { payload }) => {
        state.customFieldsLoading = false;
        state.customFields = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchCustomFields.rejected, (state) => {
        state.customFieldsLoading = false;
      })

      .addCase(removeCustomField.fulfilled, (state, { payload }) => {
        state.customFields = state.customFields.filter((f) => f.Id !== payload);
      })

      .addCase(fetchContactCustomFields.pending, (state) => {
        state.contactFieldValuesLoading = true;
      })
      .addCase(fetchContactCustomFields.fulfilled, (state, { payload }) => {
        state.contactFieldValuesLoading = false;
        state.contactFieldValues[payload.audienceId] = payload.values;
      })
      .addCase(fetchContactCustomFields.rejected, (state) => {
        state.contactFieldValuesLoading = false;
      })

      .addCase(saveContactCustomFields.fulfilled, (state, { payload }) => {
        // merge saved values back into store
        const existing = state.contactFieldValues[payload.audienceId] || [];
        payload.values.forEach(({ FieldId, Value }) => {
          const item = existing.find((v) => v.CustomFieldId === FieldId);
          if (item) item.Value = Value;
        });
        state.contactFieldValues[payload.audienceId] = existing;
      })

      // ── Activities ──
      .addCase(fetchActivities.pending, (state) => {
        state.activitiesLoading = true;
      })
      .addCase(fetchActivities.fulfilled, (state, { payload }) => {
        state.activitiesLoading = false;
        state.activities[payload.audienceId] = payload.activities;
      })
      .addCase(fetchActivities.rejected, (state) => {
        state.activitiesLoading = false;
      })

      .addCase(addActivity.fulfilled, (state, { payload }) => {
        const list = state.activities[payload.audienceId] || [];
        state.activities[payload.audienceId] = [payload.activity, ...list];
      })
      .addCase(removeActivity.fulfilled, (state, { payload }) => {
        const list = state.activities[payload.audienceId] || [];
        state.activities[payload.audienceId] = list.filter((a) => a.Id !== payload.activityId);
      })
      .addCase(completeActivity.fulfilled, (state, { payload }) => {
        const list = state.activities[payload.audienceId] || [];
        const item = list.find((a) => a.Id === payload.activityId);
        if (item) item.CompletedAt = new Date().toISOString();
      })

      // ── Pipeline ──
      .addCase(fetchPipelines.pending, (state) => {
        state.pipelinesLoading = true;
      })
      .addCase(fetchPipelines.fulfilled, (state, { payload }) => {
        state.pipelinesLoading = false;
        state.pipelines = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchPipelines.rejected, (state) => {
        state.pipelinesLoading = false;
      })

      .addCase(fetchPipelineWithDeals.pending, (state) => {
        state.pipelineLoading = true;
      })
      .addCase(fetchPipelineWithDeals.fulfilled, (state, { payload }) => {
        state.pipelineLoading = false;
        state.activePipeline = payload;
      })
      .addCase(fetchPipelineWithDeals.rejected, (state) => {
        state.pipelineLoading = false;
      })

      .addCase(addDeal.fulfilled, (state, { payload }) => {
        if (!state.activePipeline) return;
        const stage = state.activePipeline.Stages?.find((s) => s.Id === payload.StageId);
        if (stage) stage.Deals = [...(stage.Deals || []), payload];
      })

      .addCase(editDeal.fulfilled, (state, { payload }) => {
        if (!state.activePipeline) return;
        state.activePipeline.Stages?.forEach((stage) => {
          const idx = stage.Deals?.findIndex((d) => d.Id === payload.Id);
          if (idx !== undefined && idx > -1) stage.Deals[idx] = { ...stage.Deals[idx], ...payload };
        });
      })

      .addCase(removeDeal.fulfilled, (state, { payload }) => {
        if (!state.activePipeline) return;
        const stage = state.activePipeline.Stages?.find((s) => s.Id === payload.stageId);
        if (stage) stage.Deals = stage.Deals?.filter((d) => d.Id !== payload.dealId);
      })

      .addCase(fetchContactDeals.fulfilled, (state, { payload }) => {
        state.contactDeals[payload.audienceId] = payload.deals;
      })

      // ── Tags ──
      .addCase(fetchProfileTags.pending, (state) => {
        state.profileTagsLoading = true;
      })
      .addCase(fetchProfileTags.fulfilled, (state, { payload }) => {
        state.profileTagsLoading = false;
        state.profileTags = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchProfileTags.rejected, (state) => {
        state.profileTagsLoading = false;
      })

      .addCase(fetchContactTags.fulfilled, (state, { payload }) => {
        state.contactTags[payload.audienceId] = payload.tags;
      })
      .addCase(addContactTag.fulfilled, (state, { payload }) => {
        state.contactTags[payload.audienceId] = payload.tags;
        // Merge new tags into profileTags
        payload.tags.forEach((t) => {
          if (!state.profileTags.find((pt) => pt.Id === t.Id)) {
            state.profileTags.push(t);
          }
        });
      })
      .addCase(removeContactTag.fulfilled, (state, { payload }) => {
        state.contactTags[payload.audienceId] = payload.tags;
      })
      .addCase(deleteProfileTag.fulfilled, (state, { payload: tagId }) => {
        state.profileTags = state.profileTags.filter((t) => t.Id !== tagId);
        Object.keys(state.contactTags).forEach((aid) => {
          state.contactTags[aid] = state.contactTags[aid].filter((t) => t.Id !== tagId);
        });
      })

      // ── Dashboard ──
      .addCase(fetchCrmDashboard.pending, (state) => {
        state.dashboardLoading = true;
      })
      .addCase(fetchCrmDashboard.fulfilled, (state, { payload }) => {
        state.dashboardLoading = false;
        state.dashboard = payload;
      })
      .addCase(fetchCrmDashboard.rejected, (state) => {
        state.dashboardLoading = false;
      });
  },
});

export const { moveDealOptimistic, clearCrmError } = crmSlice.actions;
export default crmSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────
export const selectCustomFields = (state) => state.crm.customFields;
export const selectCustomFieldsLoading = (state) => state.crm.customFieldsLoading;
export const selectContactFieldValues = (audienceId) => (state) => state.crm.contactFieldValues[audienceId] || [];
export const selectContactFieldValuesLoading = (state) => state.crm.contactFieldValuesLoading;
export const selectActivities = (audienceId) => (state) => state.crm.activities[audienceId] || [];
export const selectActivitiesLoading = (state) => state.crm.activitiesLoading;
export const selectPipelines = (state) => state.crm.pipelines;
export const selectActivePipeline = (state) => state.crm.activePipeline;
export const selectPipelinesLoading = (state) => state.crm.pipelinesLoading;
export const selectPipelineLoading = (state) => state.crm.pipelineLoading;
export const selectContactDeals = (audienceId) => (state) => state.crm.contactDeals[audienceId] || [];
export const selectCrmDashboard = (state) => state.crm.dashboard;
export const selectCrmDashboardLoading = (state) => state.crm.dashboardLoading;
export const selectProfileTags = (state) => state.crm.profileTags;
export const selectProfileTagsLoading = (state) => state.crm.profileTagsLoading;
export const selectContactTags = (audienceId) => (state) => state.crm.contactTags[audienceId] || [];
